import * as React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { styled } from "@mui/material/styles";
import PlayerDashboard from "./PlayerDashboard.png";
import Asset1 from "../layout/Asset1_cropped.png";
import { Button } from "@mui/material";
import { keyframes } from '@mui/system';
import { useNavigate } from "react-router-dom";

const outlineTrace = keyframes`
  0% {
    border-color: transparent;
    box-shadow: none;
  }
  20% {
    border-top-color: currentColor;
  }
  40% {
    border-right-color: currentColor;
  }
  60% {
    border-bottom-color: currentColor;
  }
  80% {
    border-left-color: currentColor;
  }
  100% {
    border-color: currentColor;
    box-shadow: 0 0 0 2px currentColor;
  }
`;

interface DownloadCTAButtonProps {
  children: React.ReactNode;
}

const DownloadCTAButton: React.FC<DownloadCTAButtonProps> = ({ children }) => {
  const navigate = useNavigate();
  return (
    <Button
      variant="outlined"
      onClick={() => {
        navigate("/account-signup");
      }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '12px',
        fontWeight: 'bold',
        textTransform: 'none',
        borderWidth: 2,
        borderColor: 'secondary.main',
        color: 'secondary.main',
        px: 4,
        py: 1.5,
        fontSize: '1rem',
        transition: 'color 0.2s, background 0.2s',
        backgroundColor: 'background.paper',
        '&:before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: '12px',
          border: '2px solid',
          borderColor: 'transparent',
          pointerEvents: 'none',
          zIndex: 1,
          transition: 'border-color 0.2s',
        },
        '&:hover': {
          color: 'secondary.contrastText',
          backgroundColor: 'secondary.main',
          '&:before': {
            borderColor: 'secondary.contrastText',
            animation: `${outlineTrace} 0.6s cubic-bezier(.4,2,.6,1) forwards`,
          },
        },
        // Ensure the button content is above the animated outline
        '& .MuiButton-label': {
          position: 'relative',
          zIndex: 2,
        },
      }}
      size="large"
    >
      {children}
    </Button>
  );
};

const StyledBox = styled("div")(({ theme }) => ({
  alignSelf: "center",
  width: "100%",
  height: 400,
  marginTop: theme.spacing(8),
  borderRadius: (theme.vars || theme).shape.borderRadius,
  outline: "6px solid",
  outlineColor: "hsla(220, 25%, 80%, 0.2)",
  border: "1px solid",
  borderColor: (theme.vars || theme).palette.grey[200],
  boxShadow: "0 0 12px 8px hsla(220, 25%, 80%, 0.2)",
  backgroundImage: `url(${PlayerDashboard})`,
  /*`url(${
    process.env.TEMPLATE_IMAGE_URL || "https://mui.com"
  }/static/screenshots/material-ui/getting-started/templates/dashboard.jpg)`,*/
  backgroundSize: "cover",
  [theme.breakpoints.up("sm")]: {
    marginTop: theme.spacing(10),
    height: 700,
  },
  ...theme.applyStyles("dark", {
    boxShadow: "0 0 24px 12px hsla(210, 100%, 25%, 0.2)",
    backgroundImage: `url(${PlayerDashboard})`,
    /* `url(${
      process.env.TEMPLATE_IMAGE_URL || "https://mui.com"
    }/static/screenshots/material-ui/getting-started/templates/dashboard-dark.jpg)`,*/
    outlineColor: "hsla(220, 20%, 42%, 0.1)",
    borderColor: (theme.vars || theme).palette.grey[700],
  }),
}));

export default function Hero() {
  return (
    <Box
      id="hero"
      sx={(theme) => ({
        width: "100%",
        backgroundRepeat: "no-repeat",

        backgroundImage:
          "radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 90%), transparent)",
        ...theme.applyStyles("dark", {
          backgroundImage:
            "radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 16%), transparent)",
        }),
      })}
    >
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 6, sm: 8 },
          pb: { xs: 8, sm: 12 },
        }}
      >
        <Stack
          spacing={5}
          useFlexGap
          sx={{ alignItems: "center", width: { xs: "100%", sm: "70%" } }}
        >
          <Box
            component="img"
            src={Asset1}
            alt="Chukkerbook"
            sx={{
              height: { xs: "clamp(7rem, 25vw, 10rem)", sm: "clamp(9rem, 30vw, 12rem)", md: "clamp(11rem, 35vw, 15rem)" },
              width: "auto",
              maxWidth: "100%",
              objectFit: "contain",
              filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))",
            }}
          />
          <Typography
            sx={{
              textAlign: "center",
              color: "white",
              width: { sm: "100%", md: "80%" },
            }}
          >
            An <strong>all-in-one web application for managing polo clubs</strong>. Chukkerbook allows you to handle scheduling,
            member management, payments, analytics and more in one place. Create a club profile and set the club
            chukker schedule for the season. Members can then create an account, join your club, and quickly and easily
            sign up for their desired chukkers and lessons each week. Once all sign ups are in, Chukkerbook will generate club chukker match-ups suggestions,
            reducing manual work for the manager.
          </Typography>
          <DownloadCTAButton>SIGN UP NOW</DownloadCTAButton>
          {/*<Stack direction="row" spacing={2}>
            <DownloadCTAButton>CREATE MANAGER ACCOUNT</DownloadCTAButton>
            <DownloadCTAButton>CREATE PLAYER ACCOUNT</DownloadCTAButton>
          </Stack>*/}
        </Stack>
        <StyledBox id="image" />
      </Container>

    </Box>
  );
}
